import styles from './Slider.module.css'
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"
import prevButton from './assets/prev.png'
import nextButton from './assets/next.png'

interface sliderPropsInterface {
    images: string[]
    reverse?: boolean | undefined
    imageClassName?: string | undefined
    buttonContainerClassName?: string | undefined
    autoplay?: boolean | undefined
    autoplayDuration?: number | undefined
}

type directionType = -1 | 0 | 1

type positionType = "left" | "center" | "right"
type positionCallback = () => positionType

type enterReturnType = {
    scale: number
    x: number | string
    opacity: number
}

type centerReturnType = {
    zIndex: number
} & enterReturnType

type variantsType = {
    enter: ({ direction }: { direction: directionType }) => enterReturnType
    center: ({ position, direction }: { position: positionCallback, direction: directionType}) => centerReturnType
    exit: ({ direction }: { direction: directionType }) => enterReturnType,
}

type getZIndexType = ({ position, direction }: { position: positionCallback, direction: directionType }) => number

export default function Slider(props: sliderPropsInterface) {
    const { images, reverse, imageClassName, buttonContainerClassName, autoplay, autoplayDuration } = props
    const imagesLength = images.length
    const reverseMode = imagesLength === 2 && reverse === true

    const [autoPlay, setAutoPlay] = useState(autoplay === true)
    const [[activeIndex, direction], setActiveIndex] = useState([ 0, 0 ])
    const [dragStartX, setDragStartX] = useState(-1)
    const [dragInProgress, setDragInProgress] = useState(false)

    // we want the scope to be always to be in the scope of the array so that the carousel is endless
    const indexInArrayScope = ((activeIndex % imagesLength) + imagesLength) % imagesLength

    // so that the carousel is endless, we need to repeat the items twice
    // then, we slice the the array so that we only have 3 items visible at the same time
    const scopeCounter = imagesLength >= 3 ? 3 : imagesLength
    const visibleImages = [...images, ...images].slice(indexInArrayScope, indexInArrayScope + scopeCounter)
    
    const variants: variantsType = {
        enter: ({ direction }) => {
            return { scale: 0.2, x: direction < 1 ? 50 : -50, opacity: 0 }
        },

        center: ({ position, direction }) => {
            return {
                scale: position() === "center" ? 1 : 0.7,
                x: 0,
                zIndex: getZIndex({ position, direction }),
                opacity: 1
            }
      
        },

        exit: ({ direction }) => {
            return { scale: 0.2, x: direction < 1 ? -50 : 50, opacity: 0 }
        }
    }

    const getZIndex: getZIndexType = ({ position, direction }) => {
        const indexes = {
            left: direction > 0 ? 2 : 1,
            center: 3,
            right: direction > 0 ? 1 : 2
        }
        
        return indexes[position()]
    }

    let sliderClassName = styles.slider
    let imageClassNameTmp = styles.image
    
    if(imageClassName && imageClassName.length > 0) {
        imageClassNameTmp += ' ' + imageClassName
    }

    if(reverseMode) {
        sliderClassName += ' ' + styles.sliderRevesre
        imageClassNameTmp += ' ' + styles.reverseImage
    }

    let buttonContainerClassNameTmp = styles.buttonContainer
    if(buttonContainerClassName && buttonContainerClassName.length > 0) {
        buttonContainerClassNameTmp += ' ' + buttonContainerClassName
    }

    const handleClick = (newDirection: number, triggerByUser: boolean = true) => {
        setActiveIndex((prevIndex) => [prevIndex[0] + newDirection, newDirection])
        if(triggerByUser) {
            setAutoPlay(false)
        }
    }

    useEffect(() => {
        let interval: string | number | NodeJS.Timer | undefined

        const duration = parseInt(autoplayDuration?.toString() ?? "0")

        if(autoPlay === true && duration / 1000 > 0) {
            interval = setInterval(() => {
                handleClick(1, false)
            }, autoplayDuration)
        }

        return (() => {
            clearInterval(interval)
        })
    }, [ autoPlay, autoplayDuration ])

    useEffect(() => {
        let timeout: string | number | NodeJS.Timeout | undefined

        /**
         * autoplay -> comes from "props"
         * autoPlay -> state variable
         * autoplayDuration -> comes from "props"
         */

        const duration = parseInt(autoplayDuration?.toString() ?? "0")

        if(autoplay === true && autoPlay === false && dragInProgress === false && duration / 1000 > 0) {
            timeout = setTimeout(() => setAutoPlay(true), Math.max(duration / 2, 1000))
        }

        return(() => {
            clearTimeout(timeout)
        })
    }, [ autoPlay, dragInProgress, autoplay, autoplayDuration ])

    return (
        <div className={ styles.mainContainer }>
            <div className={ sliderClassName }>
                <AnimatePresence mode="popLayout" initial={ true }>
                    {
                        visibleImages.map(image => {
                            return (
                                <motion.img
                                    dragConstraints={{ right:0, left: 0 }}
                                    onDragStart={(_, info) => {
                                        setDragStartX(info.offset.x)
                                        setAutoPlay(false)
                                        setDragInProgress(true)
                                    }}
                                    onDragEnd={(_, info) => {
                                        dragStartX > info.offset.x ? handleClick(1) :  handleClick(-1)
                                        setAutoPlay(autoplay === true)
                                        setDragInProgress(false)
                                    }}
                                    drag={ "x" }
                                    key={ image }
                                    src={ image }
                                    className={ imageClassNameTmp }
                                    layout='preserve-aspect'
                                    custom={{
                                        direction,
                                        position: () => {
                                            if(reverseMode) {
                                                if (image === visibleImages[0]) {
                                                    return "center"
                                                }

                                                return "right"
                                            }
                                            else {
                                                if(image === visibleImages[0]) {
                                                    return "left"
                                                }
                                                else if (image === visibleImages[1]) {
                                                    return "center"
                                                }
                                                else {
                                                    return "right"
                                                }
                                            }

                                        }
                                    }}
                                    variants={ variants }
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    
                                    transition={{ duration: 1 }}
                                />
                            )
                        })
                    }
                </AnimatePresence>
            </div>

            <div className={ buttonContainerClassNameTmp }>
                <div>
                    <motion.img 
                        className={styles.btnPrev}
                        src={prevButton}
                        whileTap={{ scale: 0.8 }}
                        onClick={() => handleClick(1)}
                    >
                    </motion.img>
                </div>

                <div>
                    <motion.img 
                        className={styles.btnNext}
                        src={nextButton}
                        whileTap={{ scale: 0.8 }}
                        onClick={() => handleClick(-1)}
                    >
                    </motion.img>
                </div>
            </div>
        </div>
    )
}